import React from 'react'
import Helmet from 'react-helmet'

import config from '../utils/config'

export const SEO = ({ postNode, postPath, postSEO, customDescription }) => {
  const postMeta = postNode?.frontmatter || {}
  const author = config.author
  const siteUrl = config.siteUrl
  const datePublished = postMeta.date || ''
  const dateModified = postMeta.date_modified || datePublished

  let title
  let description
  let image = config.siteLogo
  let postURL = postPath ?? ''

  if (postSEO) {
    title = postMeta.title
    description =
      customDescription ??
      postMeta.description ??
      postNode.excerpt ??
      config.description

    if (postMeta.thumbnail) {
      image = postMeta.thumbnail.childImageSharp.fixed.src
    }
  } else {
    title = config.title
    description = customDescription || config.description
  }

  const schemaOrgJSONLD = [
    {
      '@context': 'http://schema.org',
      '@type': 'WebSite',
      url: siteUrl,
      name: config.title,
      alternateName: config.titleAlt || config.title,
    },
    {
      '@context': 'http://schema.org',
      '@type': 'Organization',
      url: siteUrl,
      name: config.organization || author,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}${config.siteLogo}`,
        width: '144',
        height: '144',
      },
    },
  ]

  image = `${siteUrl}${image}`
  postURL = `${siteUrl}${postURL}`
  if (postSEO) {
    schemaOrgJSONLD.push(
      {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': postURL,
              name: title,
              image,
            },
          },
        ],
      },
      {
        '@context': 'http://schema.org',
        '@type': 'NewsArticle',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': postURL,
        },
        url: postURL,
        name: title,
        headline: title,
        image: {
          '@type': 'ImageObject',
          url: image,
        },
        description,
        datePublished,
        dateModified,
        author: {
          '@type': 'Person',
          name: author,
        },
        publisher: {
          '@type': 'Organization',
          name: config.organization || author,
          logo: {
            '@type': 'ImageObject',
            url: `${siteUrl}${config.siteLogo}`,
            width: '144',
            height: '144',
          },
        },
      }
    )
  }

  return (
    <Helmet>
      <meta name="description" content={description} />
      <meta name="image" content={image} />
      <link rel="canonical" href={postURL} />

      {/* Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgJSONLD)}
      </script>

      {/* OpenGraph */}
      <meta property="og:site_name" content={config.title} />
      <meta property="og:url" content={postURL} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={config.language.replace('-', '_')} />

      {postSEO && <meta property="og:type" content="article" />}
      {postSEO && <meta property="article:author" content={author} />}
      {postSEO && (
        <meta property="article:published_time" content={datePublished} />
      )}
      {postSEO && (
        <meta property="article:modified_time" content={dateModified} />
      )}

      {/* Twitter */}
      <meta
        name="twitter:card"
        content={postSEO ? 'summary_large_image' : 'summary'}
      />
      <meta name="twitter:creator" content={config.twitterHandle} />
      <meta name="twitter:site" content={config.twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  )
}
